import { Form, Formik } from "formik";
import { useState } from "react";
import Button from "../UI/Button";
import Callout from "../UI/Callout";
import { BalanceDomain } from "../../utils/constants/balances";
// @ts-expect-error pure js
import TextInput from "../UI/TextInput";
import FormikSubmitButton from "../UI/FormikSubmitButton";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import BalanceSplitFields from "../Balance/BalanceSplitFields";
// @ts-expect-error pure js
import { sanitizeValues, toInitialValuesFromSchema } from "../../models/utils";
import {
  RepartitionKeyUnit,
  repartitionKeyInitialValues,
  repartitionKeySchema,
} from "../../models/financeConfig";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import type { RepartitionKey } from "../../models/financeConfig";
// @ts-expect-error pure js
import RadioGroup from "../UI/RadioGroup";
// @ts-expect-error pure js
import Radio from "../UI/Radio";
import Text from "../UI/Text";
import styles from "./RepartitionKeyForm.module.css";

interface RepartitionKeyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  repartitionKey: RepartitionKey | null;
  propertyFolderId?: number;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}

export default function RepartitionKeyForm({
  open,
  onOpenChange,
  repartitionKey,
  propertyFolderId,
  onSubmit,
  onDelete,
}: RepartitionKeyFormProps) {
  const [error, setError] = useState("");

  const initialValues = toInitialValuesFromSchema(
    repartitionKeySchema,
    repartitionKey,
    repartitionKeyInitialValues,
  );

  async function submitHandler(values: Record<string, unknown>) {
    setError("");
    try {
      await onSubmit(sanitizeValues(values));
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  async function deleteHandler() {
    if (
      !window.confirm(
        "Voulez-vous vraiment supprimer cette clé de répartition ?",
      )
    ) {
      return;
    }
    try {
      await onDelete();
    } catch (e) {
      setError(handleAPIError(e));
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={submitHandler}
    >
      {({ resetForm, values }) => (
        <ModalDialog
          size="lg"
          open={open}
          header={
            repartitionKey
              ? "Modifier une clé de répartition"
              : "Créer une clé de répartition"
          }
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              resetForm();
              setError("");
            }
            onOpenChange(nextOpen);
          }}
        >
          <Form>
            <ModalDialogBody className={styles.form}>
              <TextInput
                type="text"
                name="nom"
                placeholder="Nom de la clé"
                label="Nom"
                marginForm=""
                mandatory
              />
              <div className={styles.unit}>
                <Text weight="bold">Unité de répartition</Text>
                <RadioGroup styling="button" marginForm="">
                  <Radio
                    name="unit"
                    value={RepartitionKeyUnit.PERCENT}
                    label="Pourcentages"
                  />
                  <Radio
                    name="unit"
                    value={RepartitionKeyUnit.SHARE}
                    label="Tantièmes"
                  />
                </RadioGroup>
                <Text color="muted">
                  {values.unit === RepartitionKeyUnit.SHARE
                    ? "Saisissez les tantièmes de chaque lot (par exemple ceux du règlement de copropriété). Chaque lot reçoit sa quote-part sur le total des tantièmes."
                    : "Saisissez la part de chaque lot en pourcentage, la somme doit faire 100 %."}
                </Text>
              </div>
              {values.unit === RepartitionKeyUnit.SHARE && (
                <TextInput
                  type="number"
                  step="0.01"
                  name="total_shares"
                  label="Total des tantièmes"
                  placeholder="1000"
                  marginForm=""
                  subText="Peut dépasser la somme des lots si certains biens sortent de la clé. Laissez vide pour utiliser la somme des tantièmes saisis."
                />
              )}
              {propertyFolderId ? (
                <BalanceSplitFields
                  name="repartition"
                  parentId={propertyFolderId}
                  domaineBalance={BalanceDomain.FOLDER}
                  isFinanceConfig
                  repartitionUnit={values.unit as RepartitionKeyUnit}
                  totalShares={parseFloat(String(values.total_shares)) || 0}
                  childBalances={
                    initialValues.repartition.length > 0
                      ? initialValues.repartition
                      : undefined
                  }
                />
              ) : null}
              <Callout variant="danger">{error}</Callout>
            </ModalDialogBody>
            <ModalDialogFooter>
              {repartitionKey && (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={deleteHandler}
                >
                  Supprimer
                </Button>
              )}
              <FormikSubmitButton>Valider</FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        </ModalDialog>
      )}
    </Formik>
  );
}
